import React, { useState } from 'react';
import './project_wizard.css';
import WizardEventForm, { validateEventForm } from './wizard_event_form';
import WizardProblemForm, { validateProblemForm } from './wizard_problem_form';
import WizardSolutionForm from './wizard_solution_form';
import WizardSolutionImplementationForm from './wizard_solution_implementation_form';
import WizardReportForm from './wizard_report_form';
import { showAlert } from '../../../../functions/show_alert';
import AppBarWidget from '../../../../widgets/app_bar/app_bar';

// Event wizard steps (starting from step 2 of project wizard, skipping project name)
const STEPS = [
    { number: 1, title: 'Create Event' },
    { number: 2, title: 'Identify Problem' },
    { number: 3, title: 'Map Problem to Solution' },
    { number: 4, title: 'Solution Implementation' },
    { number: 5, title: 'Create Report' }
];

export default function EventWizard({ projectSelected, onClose, onComplete, isManual = false }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [eventData, setEventData] = useState({});
    const [problemData, setProblemData] = useState({});
    const [solutionData, setSolutionData] = useState({});
    const [solutionImplementationData, setSolutionImplementationData] = useState({});

    const handleNext = () => {
        if (currentStep === 1) {
            // Validate event form
            const validation = validateEventForm(eventData);
            if (!validation.valid) {
                showAlert(false, validation.message);
                return;
            }
        } else if (currentStep === 2) {
            // Validate problem form
            const validation = validateProblemForm(problemData);
            if (!validation.valid) {
                showAlert(false, validation.message);
                return;
            }
        } else if (currentStep === 3) {
            // Validate solution form (read-only, but check if problem data exists)
            if (!problemData.pickedPMT || problemData.pickedPMT.length === 0) {
                showAlert(false, 'Please complete the problem step first');
                return;
            }
        } else if (currentStep === 4) {
            // Validate solution implementation form (read-only, but check if solution data exists)
            if (!solutionData.solutionMappedTo || solutionData.solutionMappedTo.length === 0) {
                showAlert(false, 'Please complete the solution step first');
                return;
            }
        }
        if (currentStep < STEPS.length) {
            setCurrentStep(currentStep + 1);
        } else {
            // Wizard complete
            handleComplete();
        }
    };

    const handleEventDataChange = (data) => {
        setEventData(data);
    };

    const handleProblemDataChange = (data) => {
        setProblemData(data);
    };

    const handleSolutionDataChange = (data) => {
        setSolutionData(data);
    };

    const handleSolutionImplementationDataChange = (data) => {
        setSolutionImplementationData(data);
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleComplete = () => {
        // Validate event form before completing
        if (currentStep === 1) {
            const validation = validateEventForm(eventData);
            if (!validation.valid) {
                showAlert(false, validation.message);
                return;
            }
        }

        if (onComplete) {
            onComplete({
                eventData: eventData,
                problemData: problemData,
                solutionData: solutionData,
                solutionImplementationData: solutionImplementationData
            });
        }
    };

    const handleCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="wizard-step-content">
                        <WizardEventForm
                            projectId={projectSelected?.id}
                            onDataChange={handleEventDataChange}
                            initialData={eventData}
                        />
                    </div>
                );
            case 2:
                return (
                    <div className="wizard-step-content">
                        <WizardProblemForm
                            eventData={eventData}
                            onDataChange={handleProblemDataChange}
                            initialData={problemData}
                        />
                    </div>
                );
            case 3:
                return (
                    <div className="wizard-step-content">
                        <WizardSolutionForm
                            eventData={eventData}
                            problemData={problemData}
                            onDataChange={handleSolutionDataChange}
                            initialData={solutionData}
                        />
                    </div>
                );
            case 4:
                return (
                    <div className="wizard-step-content">
                        <WizardSolutionImplementationForm
                            eventData={eventData}
                            problemData={problemData}
                            solutionData={solutionData}
                            onDataChange={handleSolutionImplementationDataChange}
                            initialData={solutionImplementationData}
                        />
                    </div>
                );
            case 5:
                return (
                    <div className="wizard-step-content">
                        <WizardReportForm
                            eventData={eventData}
                            problemData={problemData}
                            solutionData={solutionData}
                            solutionImplementationData={solutionImplementationData}
                            onDataChange={handleEventDataChange}
                            initialData={{}}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <AppBarWidget />
            <div className="project-wizard-screen">
                <div className="project-wizard-container">
                    <div className="wizard-header">
                        <h1 className="wizard-title">Create New Event</h1>
                    </div>

                    {/* Desktop Stepper */}
                    <div className="wizard-steps-indicator desktop-stepper">
                        {STEPS.map((step, index) => (
                            <React.Fragment key={step.number}>
                                <div className={`step-indicator ${currentStep === step.number ? 'active' : currentStep > step.number ? 'completed' : ''}`}>
                                    <div className="step-number">{step.number}</div>
                                    <div className="step-title">{step.title}</div>
                                </div>
                                {index < STEPS.length - 1 && (
                                    <div className={`step-connector ${currentStep > step.number ? 'completed' : ''}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Mobile Stepper */}
                    <div className="mobile-stepper">
                        <div className="mobile-stepper-content">
                            <div className="mobile-stepper-circle">
                                <svg className="mobile-stepper-svg" viewBox="0 0 100 100">
                                    {/* Background circle */}
                                    <circle
                                        className="mobile-stepper-bg"
                                        cx="50"
                                        cy="50"
                                        r="40"
                                        fill="none"
                                        stroke="#E1E1E1"
                                        strokeWidth="5"
                                    />
                                    {/* Progress circle */}
                                    <circle
                                        className="mobile-stepper-progress"
                                        cx="50"
                                        cy="50"
                                        r="40"
                                        fill="none"
                                        stroke="#00A14C"
                                        strokeWidth="5"
                                        strokeLinecap="round"
                                        strokeDasharray={`${2 * Math.PI * 40}`}
                                        strokeDashoffset={`${2 * Math.PI * 40 * (1 - currentStep / STEPS.length)}`}
                                        transform="rotate(-90 50 50)"
                                    />
                                </svg>
                                <div className="mobile-stepper-text">
                                    <span className="mobile-stepper-current">{currentStep}</span>
                                    <span className="mobile-stepper-separator"> of </span>
                                    <span className="mobile-stepper-total">{STEPS.length}</span>
                                </div>
                            </div>
                            <div className="mobile-stepper-labels">
                                <div className="mobile-stepper-current-label">
                                    {STEPS[currentStep - 1]?.title}
                                </div>
                                {currentStep < STEPS.length && (
                                    <div className="mobile-stepper-next-label">
                                        Next: {STEPS[currentStep]?.title}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="wizard-content">
                        {renderStepContent()}
                    </div>

                    <div className="wizard-footer">
                        <button className={`wizard-button cancel ${currentStep === 1 ? 'show-on-mobile' : 'hide-on-mobile'}`} onClick={handleCancel}>
                            Cancel
                        </button>
                        {currentStep > 1 && (
                            <button className="wizard-button back" onClick={handleBack}>
                                Previous
                            </button>
                        )}
                        {isManual && currentStep === 1 && (
                            <button className="wizard-button next" onClick={handleComplete}>
                                Create Event
                            </button>
                        )}
                        {(!isManual || currentStep > 1) && (
                            <button className="wizard-button next" onClick={handleNext}>
                                {currentStep === STEPS.length ? 'Create' : 'Next'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
